import React, { useState, useEffect } from 'react';
import { Select, Toast, Button, Space } from '@douyinfe/semi-ui';
import { IconRefresh, IconMember } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import UniImage from '../UniImage';

const VipCardSelector = ({ value, onChange, placeholder, disabled, multiple = false }) => {
    const [loading, setLoading] = useState(false);
    const [cardList, setCardList] = useState([]);
    const [pagination, setPagination] = useState({ page: 0, limit: 10, total: 0 });
    const [searchKey, setSearchKey] = useState('');
    const [internalValue, setInternalValue] = useState([]);

    const typeMap = {
        1: '储值卡',
        2: '次卡',
        4: '折扣卡'
    };

    const fetchCardList = async () => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/vip/listmini_vip_card/', {
                params: {
                    page: pagination.page,
                    limit: pagination.limit,
                    key: searchKey,
                    status: 0 // 只显示正常状态的会员卡
                }
            });
            if (response.errno === 0) {
                setCardList(response.list || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.total_cnt
                }));
            } else {
                Toast.error(response.errstr || '获取会员卡列表失败');
            }
        } catch (error) {
            console.error('获取会员卡列表失败:', error);
            Toast.error('获取会员卡列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (value) {
            const valueArray = Array.isArray(value) ? value : [value];
            const validValues = valueArray.filter(v => v && v !== '0');
            setInternalValue(validValues);

            // 检查是否需要获取会员卡详情
            const missingCards = validValues.filter(uid => !cardList.some(card => card.uid === uid));
            if (missingCards.length > 0) {
                fetchCardDetails(missingCards);
            }
        } else {
            setInternalValue([]);
        }
    }, [value, cardList]);

    const fetchCardDetails = async (uids) => {
        try {
            const response = await request.get('/go/sp/vip/list_vip_card/', {
                params: {
                    uids: uids.join(',')
                }
            });
            if (response.errno === 0 && response.list) {
                setCardList(prevList => {
                    const newList = [...prevList];
                    response.list.forEach(card => {
                        if (!newList.some(existingCard => existingCard.uid === card.uid)) {
                            newList.push(card);
                        }
                    });
                    return newList;
                });
            }
        } catch (error) {
            console.error('获取会员卡详情失败:', error);
        }
    };

    useEffect(() => {
        fetchCardList();
    }, []);

    const handleChange = (selectedUids) => {
        setInternalValue(selectedUids);
        onChange?.(selectedUids);
    };

    const parseRule = (ruleStr) => {
        try {
            return typeof ruleStr === 'string' ? JSON.parse(ruleStr) : ruleStr;
        } catch (error) {
            return {};
        }
    };

    const renderCustomOption = (card) => {
        const rule = parseRule(card.rule);
        const optionStyle = {
            display: 'flex',
            paddingLeft: 24,
            paddingTop: 10,
            paddingBottom: 10,
        };
        return (
            <Select.Option
                key={card.uid}
                value={card.uid}
                style={optionStyle}
                showTick={true}
            >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                    {card.image && <UniImage src={card.image} width={40} height={40} style={{ borderRadius: 4 }} alt={card.name} />}
                    <div style={{ fontSize: '12px' }}>
                        <div style={{ color: 'var(--semi-color-text-0)', marginBottom: '4px' }}>{card.name}</div>
                    </div>
                </div>
            </Select.Option>
        );
    };

    return (
        <Select
            loading={loading}
            value={internalValue}
            onChange={handleChange}
            style={{ width: '100%' }}
            placeholder={placeholder || "请选择会员卡"}
            filter
            showClear
            multiple={multiple}
            maxTagCount={multiple ? 3 : 1}
            filterOption={(input, option) => {
                const name = option?.label?.toString() || '';
                return name.toLowerCase().includes(input.toLowerCase());
            }}
            prefix={<IconMember style={{ color: 'var(--semi-color-primary)' }} />}
            disabled={disabled}
            onDropdownVisibleChange={(visible) => {
                if (visible) {
                    setSearchKey('');
                    setPagination(prev => ({ ...prev, page: 0 }));
                    fetchCardList();
                }
            }}
            onSearch={(value) => {
                setSearchKey(value);
                setPagination(prev => ({ ...prev, page: 0 }));
                fetchCardList();
            }}
            outerBottomSlot={
                pagination.total > (pagination.page + 1) * pagination.limit && (
                    <Button
                        type="tertiary"
                        onClick={() => {
                            setPagination(prev => ({ ...prev, page: prev.page + 1 }));
                            fetchCardList();
                        }}
                        style={{ width: '100%', marginTop: '8px' }}
                    >
                        加载更多
                    </Button>
                )
            }
            outerTopSlot={
                <Button
                    icon={<IconRefresh />}
                    type="tertiary"
                    onClick={fetchCardList}
                    style={{ width: '100%', marginBottom: '8px' }}
                >
                    刷新列表
                </Button>
            }
        >
            {cardList.map(card => renderCustomOption(card))}
        </Select>
    );
};

export default VipCardSelector;